/**
 * pdf.js behind a small surface, so the placement editor never touches
 * pdfjs-dist directly and its tests can replace this whole module.
 *
 * pdfjs-dist is imported lazily: it only runs in the browser and is only
 * needed once the editor opens. The worker is loaded by URL; under Vite that
 * URL comes from a `?url` import, which emits the worker file as an asset
 * (corp-dashboard/components/esign/pdf-page-view.tsx used webpack's
 * `new URL(..., import.meta.url)`, which Vite does not resolve for a package path).
 */

import { useEffect, useRef } from 'react';

import { POINTS_TO_MM, type PageTextItem } from '@/lib/esign/geometry';

export interface PdfTextItem extends PageTextItem {
  str: string;
}

export interface PdfHandle {
  numPages: number;
  /** Page size at its natural size (rotation applied), in millimetres. */
  pageSizeMm: (pageNum: number) => Promise<{ widthMm: number; heightMm: number }>;
  /** Unrotated text items on the page, in PDF points. Empty when the page has no readable text. */
  textItems: (pageNum: number) => Promise<PdfTextItem[]>;
  /** Draw the page into the canvas at `widthPx` CSS pixels wide. */
  render: (pageNum: number, canvas: HTMLCanvasElement, widthPx: number) => { promise: Promise<void>; cancel: () => void };
  destroy: () => void;
}

type PdfJs = typeof import('pdfjs-dist');
let pdfJsPromise: Promise<PdfJs> | null = null;

function loadPdfJs(): Promise<PdfJs> {
  if (!pdfJsPromise) {
    pdfJsPromise = Promise.all([import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.min.mjs?url')]).then(([pdfjs, worker]) => {
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
      return pdfjs;
    });
    pdfJsPromise.catch(() => {
      pdfJsPromise = null;
    });
  }
  return pdfJsPromise;
}

export async function loadPdf(url: string): Promise<PdfHandle> {
  const pdfjs = await loadPdfJs();
  const task = pdfjs.getDocument({ url });
  const doc = await task.promise;

  return {
    numPages: doc.numPages,
    async pageSizeMm(pageNum) {
      const page = await doc.getPage(pageNum);
      const vp = page.getViewport({ scale: 1 });
      return { widthMm: vp.width * POINTS_TO_MM, heightMm: vp.height * POINTS_TO_MM };
    },
    async textItems(pageNum) {
      const page = await doc.getPage(pageNum);
      const content = await page.getTextContent();
      const items: PdfTextItem[] = [];
      for (const raw of content.items) {
        const it = raw as { str?: string; transform?: number[]; width?: number; height?: number };
        if (!it.transform || it.transform.length < 6 || typeof it.str !== 'string') continue;
        // Rotated text: the stamp ignores it too.
        if (it.transform[1] !== 0 || it.transform[2] !== 0) continue;
        items.push({ str: it.str, transform: it.transform, width: it.width ?? 0, height: it.height ?? 0 });
      }
      return items;
    },
    render(pageNum, canvas, widthPx) {
      let renderTask: { promise: Promise<void>; cancel: () => void } | null = null;
      let cancelled = false;
      const promise = doc.getPage(pageNum).then((page) => {
        if (cancelled) return;
        const vp1 = page.getViewport({ scale: 1 });
        const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
        const viewport = page.getViewport({ scale: (widthPx / vp1.width) * dpr });
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${widthPx}px`;
        canvas.style.height = `${viewport.height / dpr}px`;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        renderTask = page.render({ canvasContext: ctx, viewport }) as unknown as { promise: Promise<void>; cancel: () => void };
        return renderTask.promise;
      });
      return {
        promise,
        cancel: () => {
          cancelled = true;
          try {
            renderTask?.cancel();
          } catch {
            /* already finished */
          }
        },
      };
    },
    destroy() {
      void task.destroy();
    },
  };
}

export interface PdfPageViewProps {
  pdf: PdfHandle;
  pageNum: number;
  widthPx: number;
  heightPx: number;
}

/** One page of a loaded PDF drawn on a canvas. */
export function PdfPageView({ pdf, pageNum, widthPx, heightPx }: PdfPageViewProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || widthPx <= 0) return;
    const task = pdf.render(pageNum, canvas, widthPx);
    task.promise.catch(() => {
      /* cancelled by a newer render, or the page could not be drawn */
    });
    return () => task.cancel();
  }, [pdf, pageNum, widthPx]);

  return <canvas ref={canvasRef} aria-hidden="true" className="block bg-white" style={{ width: `${widthPx}px`, height: `${heightPx}px` }} />;
}
